import type { QueryClient } from '@tanstack/react-query'
import { queryOptions } from '@tanstack/react-query'
import type { InferResponseType } from 'hono/client'
import { api, unwrap } from './api'
import { authClient } from './auth'

/**
 * Workspaces are Better Auth organizations, so the list comes from its client
 * rather than our API. Route guards need it outside React, which is why this
 * exists alongside `authClient.useListOrganizations()` — the hook covers
 * components, these cover `beforeLoad`.
 */
export const workspacesQueryOptions = queryOptions({
  queryKey: ['workspaces'],
  queryFn: async () => {
    const { data } = await authClient.organization.list()
    return data ?? []
  },
  staleTime: 5 * 60 * 1000,
})

export function ensureWorkspaces(queryClient: QueryClient) {
  return queryClient.ensureQueryData({ ...workspacesQueryOptions, revalidateIfStale: true })
}

export async function ensureFirstWorkspaceSlug(queryClient: QueryClient) {
  const workspaces = await ensureWorkspaces(queryClient)
  return workspaces[0]?.slug ?? null
}

/**
 * Call after onboarding creates a workspace, and await it before navigating.
 * Removing rather than invalidating for the same reason as `refreshSession`:
 * nothing observes this query, so an invalidated entry would be handed back
 * stale and bounce the user straight back into onboarding.
 */
export async function refreshWorkspaces(queryClient: QueryClient) {
  queryClient.removeQueries({ queryKey: workspacesQueryOptions.queryKey })
  await queryClient.ensureQueryData(workspacesQueryOptions)
}

export type WorkspaceResponse = InferResponseType<(typeof api.api.workspaces)[':slug']['$get'], 200>

/**
 * The workspace behind a URL slug, with the caller's role. The API answers 404
 * both for an unknown slug and for one the caller does not belong to, so this
 * never has to tell them apart.
 */
export const workspaceQueryOptions = (slug: string) =>
  queryOptions({
    queryKey: ['workspace', slug],
    queryFn: () => unwrap<WorkspaceResponse>(api.api.workspaces[':slug'].$get({ param: { slug } })),
    staleTime: 5 * 60 * 1000,
  })
